import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import type { WithdrawalLimitProfileDto } from '@shared/api/contracts';

const usageText = (
  profile: WithdrawalLimitProfileDto,
  period: 'daily' | 'monthly',
  unavailable: string,
) => {
  const used = period === 'daily' ? profile.dailyUsedUsdt : profile.monthlyUsedUsdt;
  if (used == null || used === '') return unavailable;
  return String(used);
};

export function WithdrawalLimitsPanel() {
  const { t } = useTranslation('cabinet');
  const services = useApiServices();
  const limits = useSWR(swrKeys.withdrawals.limits(), () => services.withdrawals.getLimits());
  const profiles = limits.data?.profiles ?? [];

  return (
    <section className="surface-card space-y-3">
      <div>
        <h2 className="section-title">{t('withdrawals.limits.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('withdrawals.limits.hint')}</p>
      </div>
      {limits.isLoading ? <p className="text-sm text-muted-foreground">{t('withdrawals.limits.loading')}</p> : null}
      {!limits.isLoading && profiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('withdrawals.limits.empty')}</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        {profiles.map((profile) => (
          <article key={`${profile.scope}-${profile.assetCode ?? ''}-${profile.networkCode ?? ''}`} className="rounded-lg border border-border p-3 text-sm">
            <p className="font-medium text-foreground">
              {t(`withdrawals.limits.scope.${profile.scope}`)}
              {profile.assetCode ? ` · ${profile.assetCode}` : ''}
              {profile.networkCode ? ` · ${profile.networkCode}` : ''}
            </p>
            <p className="mt-2 text-muted-foreground">{t('withdrawals.limits.single', { amount: profile.singleUsdt })}</p>
            <p className="text-muted-foreground">
              {t('withdrawals.limits.daily', {
                amount: profile.dailyUsdt,
                usage: usageText(profile, 'daily', t('withdrawals.limits.usageUnavailable')),
              })}
            </p>
            <p className="text-muted-foreground">
              {t('withdrawals.limits.monthly', {
                amount: profile.monthlyUsdt,
                usage: usageText(profile, 'monthly', t('withdrawals.limits.usageUnavailable')),
              })}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
